import {
  GridApi,
  ColumnVisibleEvent,
  ColumnMovedEvent,
  CellSelectionChangedEvent,
} from "ag-grid-community";
import { createContext, useContext, ReactNode, useRef, MutableRefObject, useEffect } from "react";
import { action, computed, makeAutoObservable, observable, runInAction } from "mobx";
import { useLocalObservable } from "mobx-react-lite";

import { TableColumn } from "./types";

export enum ViewMode {
  COMPACT = "compact",
  EXPANDED = "expanded",
}

export interface GenericColumn {
  id: string | number;
  name: string;
}

export interface SelectedCell {
  id: number;
  columnId: number;
}

export interface TableStoreOptions<T extends GenericColumn> {
  columns: T[];
  storageKey?: string;
  getColumnId?: (column: T) => string;
  defaultViewMode?: ViewMode | null;
}

class GenericTableStore<T extends GenericColumn> {
  @observable _hiddenColumns: Record<string, boolean> = {};
  @observable _columns: T[] = [];
  @observable _storageKey = "";
  @observable _viewMode: ViewMode | null = null;
  @observable _gridApiRef: MutableRefObject<GridApi | null> | null = null;
  @observable _selectedCell: SelectedCell | null = null;
  private _getColumnId: (column: T) => string;

  constructor(options: TableStoreOptions<T>) {
    makeAutoObservable(this);

    const {
      columns,
      storageKey = "",
      getColumnId = col => String(col.id),
      defaultViewMode = null,
    } = options;

    this._columns = columns;
    this._storageKey = storageKey;
    this._getColumnId = getColumnId;
    this._viewMode = defaultViewMode;

    if (this._storageKey && this._columns.length > 0) {
      this.initializeHiddenColumns();
    }
  }

  @action
  setColumns(columns: T[]) {
    this._columns = columns;
    if (this._storageKey && this._columns.length > 0) {
      this.initializeHiddenColumns();
    }
  }

  @action
  setStorageKey(storageKey: string) {
    this._storageKey = storageKey;
    if (this._storageKey && this._columns.length > 0) {
      this.initializeHiddenColumns();
    }
  }

  @action
  initializeHiddenColumns() {
    if (!this._storageKey || !this._columns.length) {
      return;
    }

    const storedStates = localStorage.getItem(this._storageKey);
    if (storedStates) {
      try {
        const parsed = JSON.parse(storedStates);
        runInAction(() => {
          this._hiddenColumns = parsed;
        });
      } catch {
        this.setDefaultHiddenColumns();
      }
    } else {
      this.setDefaultHiddenColumns();
    }
  }

  private setDefaultHiddenColumns() {
    runInAction(() => {
      this._hiddenColumns = {};
    });
  }

  isColumnHidden = (columnName: string): boolean => this._hiddenColumns[columnName] === true;

  @action
  toggleColumnVisibility = (columnName?: string): void => {
    if (!columnName) {
      return;
    }

    const column = this._columns.find(col => col.name === columnName);
    if (!column) {
      return;
    }

    const colId = `col_${this._getColumnId(column)}`;

    runInAction(() => {
      this._hiddenColumns[columnName] = !this._hiddenColumns[columnName];
    });
    this.saveToLocalStorage();
    this._gridApiRef?.current?.setColumnsVisible([colId], !this._hiddenColumns[columnName]);
  };

  @action
  showAllColumns = (): void => {
    const hiddenColumnNames = Object.keys(this._hiddenColumns);
    const colIds = hiddenColumnNames
      .map(columnName => {
        const column = this._columns.find(col => col.name === columnName);
        return column ? `col_${this._getColumnId(column)}` : null;
      })
      .filter(Boolean) as string[];

    this._gridApiRef?.current?.setColumnsVisible(colIds, true);
    runInAction(() => {
      this._hiddenColumns = {};
    });
    this.saveToLocalStorage();
  };

  @action
  setHiddenColumns = (columnNames: string[]): void => {
    const colIds = columnNames
      .map(columnName => {
        const column = this._columns.find(col => col.name === columnName);
        return column ? `col_${this._getColumnId(column)}` : null;
      })
      .filter(Boolean) as string[];

    runInAction(() => {
      this._hiddenColumns = {};
      columnNames.forEach(columnName => {
        this._hiddenColumns[columnName] = true;
      });
    });
    this.saveToLocalStorage();
    this._gridApiRef?.current?.setColumnsVisible(colIds, false);
  };

  @action
  addHiddenColumns = (columnNames: string[]): void => {
    const colIds = columnNames
      .map(columnName => {
        const column = this._columns.find(col => col.name === columnName);
        return column ? `col_${this._getColumnId(column)}` : null;
      })
      .filter(Boolean) as string[];

    runInAction(() => {
      columnNames.forEach(columnName => {
        this._hiddenColumns[columnName] = true;
      });
    });
    this.saveToLocalStorage();
    this._gridApiRef?.current?.setColumnsVisible(colIds, false);
  };

  get hiddenColumnsList(): string[] {
    return Object.keys(this._hiddenColumns).filter(
      columnName => this._hiddenColumns[columnName] === true,
    );
  }

  get totalHiddenColumns(): number {
    return this.hiddenColumnsList.length;
  }

  saveToLocalStorage(): void {
    if (!this._storageKey) {
      return;
    }
    localStorage.setItem(this._storageKey, JSON.stringify(this._hiddenColumns));
  }

  setGridApiRef(
    gridApiRef: MutableRefObject<GridApi | null>,
    onColumnMoved?: (event: ColumnMovedEvent) => void,
  ): void {
    this._gridApiRef = gridApiRef;
    if (gridApiRef.current) {
      this.setupGridEventListeners(onColumnMoved);
    }
  }

  @action
  setViewMode(mode: ViewMode | null): void {
    this._viewMode = mode;
    this.updateRowHeight();
  }

  @action
  setSelectedCell(cellPosition: SelectedCell | null): void {
    this._selectedCell = cellPosition;
  }

  updateRowHeight(): void {
    if (!this._gridApiRef?.current) {
      // eslint-disable-next-line no-console
      console.warn("Grid API not available for row height update");
      return;
    }

    const gridApi = this._gridApiRef.current;

    gridApi.resetRowHeights();
    gridApi.refreshCells();
    gridApi.redrawRows();
  }

  get currentRowHeight(): number {
    return this._viewMode === ViewMode.COMPACT ? 48 : 156;
  }

  setupGridEventListeners(onColumnMoved?: (event: ColumnMovedEvent) => void): void {
    if (!this._gridApiRef?.current) {
      return;
    }

    const gridApi = this._gridApiRef.current;

    const handleColumnVisible = (event: ColumnVisibleEvent) => {
      if (event.columns) {
        const allColumns = gridApi.getColumns();
        if (!allColumns) {
          return;
        }

        const currentlyHiddenColumns: string[] = [];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        allColumns.forEach((column: any) => {
          if (!column.isVisible()) {
            const colId = column.getColId()?.replace("col_", "");
            const reportColumn = this._columns.find((col: T) => this._getColumnId(col) === colId);

            if (reportColumn) {
              currentlyHiddenColumns.push(reportColumn.name);
            }
          }
        });

        this.syncToLocalStorage(currentlyHiddenColumns);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        this.setSelectedCell(null);
        gridApi.clearCellSelection();
      }
    };

    const handleColumnMoved = (event: ColumnMovedEvent) => {
      if (onColumnMoved) {
        onColumnMoved(event);
      }
    };

    const handleCellSelectionChanged = (event: CellSelectionChangedEvent) => {
      if (!event.started) {
        return;
      }

      const focusedCell = gridApi.getFocusedCell();
      if (focusedCell) {
        const { rowIndex } = focusedCell;
        const columnId = focusedCell.column.getColId()?.replace("col_", "");
        const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
        const cell = rowNode?.data[columnId];
        this.setSelectedCell({
          id: cell.cell.id,
          columnId: cell.columnId,
        });
      }
    };

    gridApi.addEventListener("columnVisible", handleColumnVisible);
    gridApi.addEventListener("columnMoved", handleColumnMoved);
    gridApi.addEventListener("cellSelectionChanged", handleCellSelectionChanged);
    document.addEventListener("keydown", handleEscapeKey);

    this.cleanupGridEvents = () => {
      gridApi.removeEventListener("columnVisible", handleColumnVisible);
      gridApi.removeEventListener("columnMoved", handleColumnMoved);
      gridApi.removeEventListener("cellSelectionChanged", handleCellSelectionChanged);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }

  private syncToLocalStorage(hiddenColumnNames: string[]): void {
    runInAction(() => {
      this._hiddenColumns = {};
      hiddenColumnNames.forEach(columnName => {
        this._hiddenColumns[columnName] = true;
      });
    });
    this.saveToLocalStorage();
  }

  private cleanupGridEvents?: () => void;

  cleanup(): void {
    if (this.cleanupGridEvents) {
      this.cleanupGridEvents();
    }
  }

  @computed
  get hiddenColumns(): string[] {
    return Object.keys(this._hiddenColumns).filter(
      columnName => this._hiddenColumns[columnName] === true,
    );
  }

  @computed
  get viewMode(): ViewMode | null {
    return this._viewMode;
  }

  @computed
  get columns(): T[] {
    return this._columns;
  }

  @computed
  get selectedCell(): SelectedCell | null {
    return this._selectedCell;
  }
}

export interface GridContextType<T extends GenericColumn> {
  gridApiRef: MutableRefObject<GridApi | null>;
  tableStore: GenericTableStore<T> | null;
  onColumnMoved?: (event: ColumnMovedEvent) => void;
  showAllColumns: () => void;
}

export function createGridContext<T extends GenericColumn>() {
  return createContext<GridContextType<T> | null>(null);
}

export interface GridContextProviderProps<T extends GenericColumn> {
  children: ReactNode;
  columns: T[];
  storageKey?: string;
  getColumnId?: (column: T) => string;
  onColumnMoved?: (event: ColumnMovedEvent) => void;
  customActions?: Record<string, () => void>;
  defaultViewMode?: ViewMode | null;
}

export const TableV2GridContext = createGridContext<GenericColumn>();

export function TableV2GridContextProvider<T extends GenericColumn>({
  children,
  columns,
  storageKey,
  getColumnId,
  onColumnMoved,
  customActions = {},
  defaultViewMode,
}: {
  children: ReactNode;
  columns: T[];
  storageKey?: string;
  getColumnId?: (column: TableColumn<T>) => string;
  onColumnMoved?: (event: ColumnMovedEvent) => void;
  customActions?: Record<string, () => void>;
  defaultViewMode?: ViewMode | null;
}) {
  const gridApiRef = useRef<GridApi | null>(null);

  const tableStore = useLocalObservable(
    () =>
      new GenericTableStore<GenericColumn>({
        columns: columns.map(col => ({ id: col.id, name: col.name })),
        storageKey,
        getColumnId: getColumnId
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            col => getColumnId(col as any)
          : col => String(col.id),
        defaultViewMode,
      }),
    [columns, storageKey],
  );

  const showAllColumns = () => {
    tableStore.showAllColumns();
  };

  useEffect(
    () => () => {
      tableStore.cleanup();
    },
    [tableStore],
  );

  return (
    <TableV2GridContext.Provider
      value={{
        gridApiRef,
        tableStore,
        onColumnMoved,
        showAllColumns,
        ...customActions,
      }}
    >
      {children}
    </TableV2GridContext.Provider>
  );
}

export const useTableV2GridContext = () => {
  const context = useContext(TableV2GridContext);

  if (!context) {
    throw new Error("You need to use this hook within a GridContextProvider");
  }

  return context;
};
